import {Component, OnDestroy, OnInit} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {ErrorModel} from '../../../app.model';
import {ApiIdsWithNoDelete, DistributionAccount} from '../../../distribution-account/distribution-account.model';
import {SubSink} from 'subsink';
import {Observable} from 'rxjs';
import {ActionsSubject, Store} from '@ngrx/store';
import {
  deleteSegment,
  segmentDeleted,
  segmentDeletionError,
  SegmentWithAccount,
  selectSegmentDetailsWithTargets
} from '../../segments.state';
import {Actions, ofType} from '@ngrx/effects';
import {Segment} from '../../segment.model';
import {TargetAccount} from '../../../target/target.model';
import {map} from 'rxjs/operators';

@Component({
  selector: 'app-segment-delete',
  template: `
    <div class="dialog-container">
      <button mat-icon-button
              id="button-dialog-close"
              class="mat-icon-button-small"
              data-cy="button-dialog-close"
              mat-dialog-close>
        <mat-icon>clear</mat-icon>
      </button>
      <ng-container *ngIf="segmentWithAccounts$ | async as segmentWithAccount">
        <h5 mat-dialog-title>Delete Segment "{{segmentWithAccount.details?.segment?.name}}"?</h5>
        <div mat-dialog-content class="mat-body-1">
          <p [hidden]="!errorMessage" id="error-feedback" class="error">{{errorMessage}}</p>
          <ng-container *ngIf="accountsToBeRemovedManually$ | async as accountsToBeRemovedManually">
            <div *ngIf="accountsToBeRemovedManually && accountsToBeRemovedManually.length > 0" style="color: red;">
              <p>You will have to delete the segment from the following target accounts manually:</p>
              <ul>
                <li *ngFor="let account of accountsToBeRemovedManually">{{account}}</li>
              </ul>
            </div>
          </ng-container>
          <p class="dialog-message">Do you really want to delete this segment?</p>
        </div>
        <div mat-dialog-actions>
          <button name="cancel" id="button-delete-cancel" color="primary" mat-raised-button mat-dialog-close>Cancel
          </button>
          <button name="ok"
                  id="button-delete-ok"
                  data-cy="button-segment-delete-ok"
                  color="warn"
                  mat-raised-button
                  (click)="delete(segmentWithAccount.details.segment)"
                  [disabled]="loading">
            <ng-container *ngIf="loading; else deleteText">
              <mat-spinner *ngIf="loading" [diameter]="32"></mat-spinner>
            </ng-container>
          </button>
        </div>
      </ng-container>
    </div>
    <ng-template #deleteText>Delete</ng-template>
  `
})
export class SegmentDeleteComponent implements OnInit, OnDestroy {
  segmentWithAccounts$: Observable<{ details: SegmentWithAccount | undefined; allTargets: TargetAccount[]; }> =
    this.store.select(selectSegmentDetailsWithTargets);
  loading = true;
  accountsToBeRemovedManually$: Observable<string[]> = this.segmentWithAccounts$
    .pipe(
      map(({details, allTargets}) => {
        this.loading = false;
        const distributionAccountsWithNoDeleteApi = allTargets
          .filter((target: TargetAccount) => target.accounts.find(acc => ApiIdsWithNoDelete.includes(acc.apiId)))
          .flatMap(target => target.accounts);
        const accountsSharedWithSegment = details?.accounts.flatMap(target => target.accounts).map(acc => acc.id) || [];
        return distributionAccountsWithNoDeleteApi
          .filter((acc: DistributionAccount) => accountsSharedWithSegment.includes(acc.id))
          .map((acc: DistributionAccount) => acc.description);
      }));
  subSink = new SubSink();
  errorMessage = '';

  constructor(private store: Store,
              private actions: ActionsSubject,
              private dialogRef: MatDialogRef<SegmentDeleteComponent>) {
  }

  ngOnInit(): void {
    this.subSink.sink = this.actions
      .pipe(ofType(segmentDeleted))
      .subscribe(_ => {
        this.dialogRef.close();
      });

    this.subSink.sink = this.actions
      .pipe(ofType(segmentDeletionError))
      .subscribe(action => {
        const error: ErrorModel = action.error;
        if (error) {
          this.loading = false;
          this.errorMessage = 'Delete failed';
          if (error.status === 400) {
            this.errorMessage = error.message;
          }
          if (error.status === 401) {
            this.errorMessage = 'Delete not allowed';
          }
        }
      });
  }

  ngOnDestroy() {
    this.subSink.unsubscribe();
  }

  delete(segment: Segment) {
    this.loading = true;
    this.store.dispatch(deleteSegment({segment}));
  }

}
